import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Box,
  InputAdornment,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import {
  CheckCircle,
  Clock,
  AlertCircle,
  Users,
  Search,
  Plus,
  Calendar,
  Play,
} from "lucide-react";
import axios from "axios";
import { getToken } from "../auth/authConfig";

interface Employee {
  id: string;
  name: string;
  department: string;
  lastDay: string;
  ktStatus: "not-started" | "in-progress" | "completed";
  role: string;
  daysRemaining: number;
  Email: string;
}

interface APIEmployee {
  EmployeeName: string;
  Department: string;
  KT_Status: string;
  LastDay: string;
  employee_id: number;
  employee_email: string;
}
interface DashboardData {
  kt_completed: number;
  kt_in_progress: number;
  kt_not_started: number;
  total_empy_exit: number;
}

const AdminHome: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingMember, setLoadingMember] = useState(false);

  const [empdata, setEmpData] = useState<Employee[]>([]);
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [newEmployee, setNewEmployee] = useState({
    EmployeeName: "",
    EmployeeEmail: "",
    Department: "",
    manager_email: "",
    LastDay: "",
    EmployeeID: "",
    BusinessUnit: "",
    PrimaryLocation: "",
    Tenure: "",
    RoleTitle: "",
  });

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 5;

  const formatKTStatus = (
    status: string
  ): "not-started" | "in-progress" | "completed" => {
    const formatted = status.toLowerCase().replace(/\s/g, "-");
    if (formatted === "in-progress") return "in-progress";
    if (formatted === "completed") return "completed";
    return "not-started";
  };

  const calculateDaysRemaining = (lastDay: string) => {
    const today = new Date();
    const lastDayDate = new Date(lastDay);
    const diffTime = lastDayDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const listOfEmployee = async () => {
    try {
      setLoading(true);
      const baseurl = `${process.env.REACT_APP_WEB_TOOL_API_BASE_URL}/employee_list`;
      const token = await getToken();

      const response = await axios.get(baseurl, {
        headers: { "oidc-token": token },
      });

      if (response.status === 200) {
        const {
          data,
          dashboard,
        }: { data: APIEmployee[]; dashboard: DashboardData } = response.data;

        const transformed: Employee[] = data.map((emp: APIEmployee) => ({
          id: emp.employee_id.toString(),
          name: emp.EmployeeName,
          department: emp.Department,
          Email: emp.employee_email,
          lastDay: emp.LastDay,
          role: "Employee", // Default placeholder
          daysRemaining: calculateDaysRemaining(emp.LastDay),
          ktStatus: formatKTStatus(emp.KT_Status),
        }));

        setEmpData(transformed);
        setEmployees(transformed);
        setDashboard(dashboard);
      }
    } catch (error) {
      console.error("Error fetching employee list:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    listOfEmployee();
  }, []);

  // const handleAddEmployee = async () => {
  //   try {
  //     const baseurl = `${process.env.REACT_APP_WEB_TOOL_API_BASE_URL}/onboarding123`;
  //     const token = await getToken();
  //     setLoadingMember(true);
  //     const response = await axios.post(
  //       baseurl,
  //       {
  //         Department: newEmployee.Department,
  //         EmployeeEmail: newEmployee.EmployeeEmail,
  //         manager_email: newEmployee.manager_email,
  //         LastDay: newEmployee.LastDay,
  //       },
  //       {
  //         headers: { "oidc-token": token },
  //       }
  //     );

  //     if (response.status === 201) {
  //       setLoadingMember(false);
  //       listOfEmployee();
  //       setIsDialogOpen(false);
  //       setNewEmployee({
  //         EmployeeEmail: "",
  //         Department: "",
  //         manager_email: "",
  //         LastDay: "",
  //         employee_email: "",
  //       });
  //     }
  //   } catch (error) {
  //     setLoadingMember(false);
  //     console.error("Error adding employee:", error);
  //   }
  // };

  const handleAddEmployee = async () => {
    const {
      EmployeeName,
      EmployeeEmail,
      Department,
      manager_email,
      LastDay,
      EmployeeID,
      BusinessUnit,
      PrimaryLocation,
      Tenure,
      RoleTitle,
    } = newEmployee;

    // Basic front-end validation
    if (
      !EmployeeName ||
      !EmployeeEmail ||
      !Department ||
      !manager_email ||
      !LastDay ||
      !EmployeeID ||
      !BusinessUnit ||
      !PrimaryLocation ||
      !Tenure ||
      !RoleTitle
    ) {
      alert("Please fill in all required fields.");
      return;
    }

    try {
      const baseurl = `${process.env.REACT_APP_WEB_TOOL_API_BASE_URL}/onboarding`;
      const token = await getToken();
      setLoadingMember(true);

      const response = await axios.post(
        baseurl,
        {
          EmployeeName,
          EmployeeEmail: EmployeeEmail.toLowerCase(),
          Department,
          manager_email: manager_email.toLowerCase(),
          LastDay,
          EmployeeID,
          BusinessUnit,
          PrimaryLocation,
          Tenure,
          RoleTitle,
        },
        {
          headers: { "oidc-token": token },
        }
      );

      if (response.status === 201) {
        setLoadingMember(false);
        listOfEmployee();
        setIsDialogOpen(false);
        // Reset all fields
        setNewEmployee({
          EmployeeName: "",
          EmployeeEmail: "",
          Department: "",
          manager_email: "",
          LastDay: "",
          EmployeeID: "",
          BusinessUnit: "",
          PrimaryLocation: "",
          Tenure: "",
          RoleTitle: "",
        });
      }
    } catch (error) {
      setLoadingMember(false);
      console.error("Error adding employee:", error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800 border-green-200";
      case "in-progress":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      default:
        return "bg-red-100 text-red-800 border-red-200";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="w-4 h-4" />;
      case "in-progress":
        return <Clock className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  const getUrgencyColor = (days: number) => {
    if (days <= 7) return "text-red-600 font-semibold";
    if (days <= 14) return "text-orange-600 font-medium";
    return "text-gray-600";
  };

  const handleStartKT = (id: string) => {
    navigate(`/exit-kt-details/${id}`);
  };

  const filteredEmployees = employees.filter(
    (emp) =>
      emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.department.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredEmployees.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredEmployees.length / rowsPerPage);

  if (loading) {
    return (
      <div
        role="status"
        style={{
          position: "absolute",
          left: "0px",
          right: "0px",
          top: "0px",
          bottom: "0px",
          background: "rgba(0,0,0,0.8)",
          zIndex: "99999",
          display: "grid",
          alignContent: "center",
          justifyContent: "center",
          justifyItems: "center",
          alignItems: "center",
        }}
      >
        <svg
          aria-hidden="true"
          className="inline w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-purple-600"
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor"
          />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentFill"
          />
        </svg>
        <span className="pt-4 text-white">
          Loading HR Dashboad, Please wait...
        </span>
      </div>
    );
  }
  return (
    <div className="p-6 welcome-screen">
      {/* Header */}
      <div className="mb-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">
            Exit Management Dashboard
          </h1>
          <p className="text-gray-600 mt-2">
            Manage knowledge transfer for exiting employees
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Exits</p>
                <p className="text-2xl font-bold text-gray-900">
                  {dashboard?.total_empy_exit}
                </p>
              </div>
              <Users className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Not Started</p>
                <p className="text-2xl font-bold text-red-600">
                  {dashboard?.kt_not_started}
                </p>
              </div>
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">In Progress</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {dashboard?.kt_in_progress}
                </p>
              </div>
              <Clock className="w-8 h-8 text-yellow-600" />
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Completed</p>
                <p className="text-2xl font-bold text-green-600">
                  {dashboard?.kt_completed}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Employees Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Current & Upcoming Exits
            </h2>
            <p className="text-gray-600 mt-1">
              Manage knowledge transfer for departing employees
            </p>
          </div>
          <div className="flex items-center gap-4">
            <TextField
              size="small"
              placeholder="Search employees..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search className="w-4 h-4 text-gray-400" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 250 }}
            />
            <Button
              variant="contained"
              startIcon={<Plus className="w-4 h-4" />}
              onClick={() => setIsDialogOpen(true)}
              sx={{
                backgroundColor: "#bb1ccc",
                "&:hover": { backgroundColor: "#bb1ccc" },
              }}
            >
              Add Member
            </Button>
          </div>
        </div>

        <div className="">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employee
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Department
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Day
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Days Remaining
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  KT Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentRows &&
                currentRows.length > 0 &&
                currentRows.map((employee) => (
                  <tr
                    key={employee.id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {employee.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {employee.Email}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {employee.department}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-900">
                          {new Date(employee.lastDay)
                            .toLocaleDateString("en-GB", {
                              weekday: "short",
                              day: "2-digit",
                              month: "long",
                              year: "numeric",
                            })
                            .replace(",", "")}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`text-sm ${getUrgencyColor(
                          employee.daysRemaining
                        )}`}
                      >
                        {employee.daysRemaining} days
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                          employee.ktStatus
                        )}`}
                      >
                        {getStatusIcon(employee.ktStatus)}
                        {employee.ktStatus
                          .replace("-", " ")
                          .replace(/\b\w/g, (l) => l.toUpperCase())}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        disabled={
                          employee.ktStatus === "completed" ||
                          employee.ktStatus === "in-progress"
                        }
                        onClick={() => handleStartKT(employee.id)}
                        style={{
                          minWidth: "140px",
                          textAlign: "center",
                          justifyItems: "center",
                          justifyContent: "center",
                        }}
                        className={`inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-medium rounded-lg shadow-sm transition-colors text-center ${
                          employee.ktStatus === "not-started"
                            ? "bg-blue-600 hover:bg-blue-700"
                            : employee.ktStatus === "in-progress"
                            ? "bg-gray-400 hover:bg-gray-400"
                            : "bg-green-600 hover:bg-green-700"
                        }`}
                      >
                        {employee.ktStatus === "not-started" && (
                          <Play className="w-4 h-4" />
                        )}
                        {employee.ktStatus === "in-progress" && (
                          <Clock className="w-4 h-4" />
                        )}
                        {employee.ktStatus === "completed" && (
                          <CheckCircle className="w-4 h-4" />
                        )}
                        {employee.ktStatus === "not-started"
                          ? "Start New"
                          : employee.ktStatus === "in-progress"
                          ? "In Progress"
                          : "Completed"}
                      </button>
                    </td>
                  </tr>
                ))}
              {currentRows.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-4 text-center text-gray-500"
                  >
                    <div className="text-center py-12">
                      <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500 text-lg">
                        No employees found
                      </p>
                      <p className="text-gray-400">
                        Try adjusting your search or filters
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 flex items-center justify-between border-t border-gray-100 text-sm text-gray-600">
          <span>
            Showing {indexOfFirstRow + 1} to{" "}
            {Math.min(indexOfLastRow, filteredEmployees.length)} of{" "}
            {filteredEmployees.length} entries
          </span>
          <div className="flex gap-2">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => prev - 1)}
              className="px-3 py-1 rounded border text-gray-700 hover:bg-gray-100 disabled:opacity-50"
            >
              Previous
            </button>
            {[...Array(totalPages)].map((_, idx) => (
              <button
                key={idx}
                className={`px-3 py-1 rounded border ${
                  currentPage === idx + 1
                    ? "bg-blue-600 text-white"
                    : "hover:bg-gray-100 text-gray-700"
                }`}
                onClick={() => setCurrentPage(idx + 1)}
              >
                {idx + 1}
              </button>
            ))}
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((prev) => prev + 1)}
              className="px-3 py-1 rounded border text-gray-700 hover:bg-gray-100 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Add Member Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: "70%",
            maxWidth: "70%",
          },
        }}
      >
        <DialogTitle>Add New Team Member</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 3,
              pt: 1,
            }}
          >
            <div style={{ display: "grid", gap: "4px" }}>
              <label>
                Employee Name <span style={{ color: "red" }}>*</span>
              </label>
              <TextField
                placeholder="Enter Employee Name"
                value={newEmployee.EmployeeName}
                onChange={(e) =>
                  setNewEmployee({
                    ...newEmployee,
                    EmployeeName: e.target.value,
                  })
                }
                fullWidth
                required
                InputLabelProps={{ required: false }}
              />
            </div>

            <div style={{ display: "grid", gap: "4px" }}>
              <label>
                Employee Email <span style={{ color: "red" }}>*</span>
              </label>
              <TextField
                placeholder="Enter Employee Email"
                value={newEmployee.EmployeeEmail}
                onChange={(e) =>
                  setNewEmployee({
                    ...newEmployee,
                    EmployeeEmail: e.target.value,
                  })
                }
                fullWidth
                required
                InputLabelProps={{ required: false }}
              />
            </div>

            <div style={{ display: "grid", gap: "4px" }}>
              <label>
                Employee ID <span style={{ color: "red" }}>*</span>
              </label>
              <TextField
                placeholder="Employee ID"
                value={newEmployee.EmployeeID}
                onChange={(e) =>
                  setNewEmployee({ ...newEmployee, EmployeeID: e.target.value })
                }
                fullWidth
                required
              />
            </div>

            <div style={{ display: "grid", gap: "4px" }}>
              <label>
                Department <span style={{ color: "red" }}>*</span>
              </label>
              <FormControl fullWidth required>
                <Select
                  value={newEmployee.Department}
                  onChange={(e) =>
                    setNewEmployee({
                      ...newEmployee,
                      Department: e.target.value,
                    })
                  }
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    Select Department
                  </MenuItem>{" "}
                  <MenuItem value="Finance">Finance</MenuItem>
                  <MenuItem value="Engineering">Engineering</MenuItem>
                  <MenuItem value="Marketing">Marketing</MenuItem>
                  <MenuItem value="Operations">Operations</MenuItem>
                  <MenuItem value="Legal">Legal</MenuItem>
                  <MenuItem value="HR">HR</MenuItem>
                  <MenuItem value="Sales">Sales</MenuItem>
                </Select>
              </FormControl>
            </div>

            <div style={{ display: "grid", gap: "4px" }}>
              <label>
                Manager Email <span style={{ color: "red" }}>*</span>
              </label>
              <TextField
                placeholder="Manager Email"
                value={newEmployee.manager_email}
                onChange={(e) =>
                  setNewEmployee({
                    ...newEmployee,
                    manager_email: e.target.value,
                  })
                }
                fullWidth
                required
              />
            </div>

            <div style={{ display: "grid", gap: "4px" }}>
              <label>
                Last Working Day <span style={{ color: "red" }}>*</span>
              </label>
              <TextField
                // label="Last Working Day"
                type="date"
                value={newEmployee.LastDay}
                onChange={(e) =>
                  setNewEmployee({ ...newEmployee, LastDay: e.target.value })
                }
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
              />
            </div>

            <div style={{ display: "grid", gap: "4px" }}>
              <label>
                Business Unit <span style={{ color: "red" }}>*</span>
              </label>
              <TextField
                placeholder="Business Unit"
                value={newEmployee.BusinessUnit}
                onChange={(e) =>
                  setNewEmployee({
                    ...newEmployee,
                    BusinessUnit: e.target.value,
                  })
                }
                fullWidth
                required
              />
            </div>

            <div style={{ display: "grid", gap: "4px" }}>
              <label>
                Primary Location <span style={{ color: "red" }}>*</span>
              </label>
              <TextField
                placeholder="Primary Location"
                value={newEmployee.PrimaryLocation}
                onChange={(e) =>
                  setNewEmployee({
                    ...newEmployee,
                    PrimaryLocation: e.target.value,
                  })
                }
                fullWidth
                required
              />
            </div>

            <div style={{ display: "grid", gap: "4px" }}>
              <label>
                Tenure <span style={{ color: "red" }}>*</span>
              </label>
              <TextField
                placeholder="Tenure"
                value={newEmployee.Tenure}
                onChange={(e) =>
                  setNewEmployee({ ...newEmployee, Tenure: e.target.value })
                }
                fullWidth
                required
              />
            </div>

            <div style={{ display: "grid", gap: "4px" }}>
              <label>
                Role Title <span style={{ color: "red" }}>*</span>
              </label>
              <TextField
                placeholder="Role Title"
                value={newEmployee.RoleTitle}
                onChange={(e) =>
                  setNewEmployee({ ...newEmployee, RoleTitle: e.target.value })
                }
                fullWidth
                required
              />
            </div>

            <Box
              sx={{
                gridColumn: "1 / -1",
                display: "flex",
                gap: 2,
                justifyContent: "flex-end",
                mt: 2,
              }}
            >
              <Button
                onClick={() => setIsDialogOpen(false)}
                variant="outlined"
                style={{ color: "#bb1ccc", borderColor: "#bb1ccc" }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddEmployee}
                variant="contained"
                disabled={loadingMember}
                sx={{
                  backgroundColor: "#bb1ccc",
                  "&:hover": { backgroundColor: "#bb1ccc" },
                }}
              >
                {loadingMember ? "Adding.." : "Add Member"}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminHome;
